import { uniq, uniqBy } from "lodash";
import { LatLng } from "geolocation-utils";

import { api } from "api";
import { Utils } from "utils";
import { Circle } from "services";
import { DEFAULT_ROUTE_DAY_HOURS } from "constants/constants";
import { IPlace, ITag, Iterable, Prettify, IGrouppedPlace } from "types";

import { filterPlaces, setBreaktimes } from "./helpers";

type Request = {
  daysCount: number;
  roadTimeInMinutes: number;
  options?: {
    roadDayTime?: number;
  };
};

type Response = IGrouppedPlace[];

export class RoutePlaces {
  /* Private Varriables */
  private categoryTags: ITag[] = [];
  private list: Iterable<IPlace>[] = [];
  private lastRequest?: Request = undefined;
  private lastResponse?: Response = undefined;

  /* Public Variables */
  public selectedTags: string[] = [];
  public countryCodes: string[] = [];
  public prioritizedTags: string[] = [];

  /* Public Methods */

  public getLastResponse() {
    return this.lastResponse;
  }

  public getAllPlaces() {
    return this.list;
  }

  public async fetch() {
    if (this.selectedTags.length === 0) {
      return Promise.reject(
        new Error("Please, select some tags for fetching places!")
      );
    }

    await this.fetchCategories();

    if (this.categoryTags.length === 0) {
      return Promise.reject(
        new Error("Cateogories didn't fetched. Something wrong with Firestore!")
      );
    }

    if (this.countryCodes.length === 0) {
      return Promise.reject(
        new Error("Please, provide country codes for fetching places!")
      );
    }

    const tagsList = this.categoryTags.map((item) => item.name);
    const response = await api.firestore.routes.places(
      this.countryCodes,
      tagsList
    );

    const filtered = filterPlaces(response);

    const neighborhoodRadius = 1; // 1 km;
    const filterdByNeighborhood = Utils.Places.filterByNeighborhood(
      filtered,
      neighborhoodRadius
    );
    const withBreaktimes = setBreaktimes(
      filterdByNeighborhood,
      this.categoryTags
    );

    this.list = withBreaktimes;
  }

  public filterAlongCoordinates(
    coordinates: LatLng[],
    radius: number,
    gaps?: [number, number]
  ) {
    const roadGaps = gaps ?? [0, 0];

    this.list = [
      ...Utils.Places.getPlacesAlongCoordinates({
        coordinates,
        gaps: roadGaps,
        places: this.list,
        searchRadius: radius,
      }),
    ];
  }

  public filterInCircle(center: LatLng, radius: number) {
    const circle = new Circle(center, radius);

    this.list = this.list.filter((item) => {
      const location = { lat: item.latitude, lng: item.longitude };
      return circle.isInside(location);
    });
  }

  /**
   *
   * @param daysCount
   * @param roadTimeInMinutes
   * @param options
   * @param options.roadDayTime How much hours would be in one route day (default 9.5 hours in one day);
   * @returns
   */
  public selectForRoute(props: Prettify<Request>) {
    const { daysCount, roadTimeInMinutes, options } = props;
    const { roadDayTime = DEFAULT_ROUTE_DAY_HOURS } = options ?? {};

    const maxPlacesCount = 76;
    const totalDurationInMinutes = daysCount * roadDayTime * 60;
    const maxTime = totalDurationInMinutes - roadTimeInMinutes;

    let response: IGrouppedPlace[] = Utils.Places.groupPlacesByTags(
      this.list,
      this.categoryTags
    ).map((item) => {
      const sortedByRating = Utils.Places.sortByCustomRating(item.places);
      const sortedByCountries = Utils.Places.sortByCountries(
        sortedByRating,
        this.countryCodes
      );

      return {
        selected: [],
        tag: item.tag,
        available: sortedByCountries,
      };
    });

    const placesIds = uniqBy(
      response.map((item) => item.available).flat(1),
      "id"
    );

    if (this.prioritizedTags.length > 0) {
      response = response.sort((a, b) => {
        const aPrior = this.prioritizedTags.includes(a.tag) ? 1 : 0;
        const bPrior = this.prioritizedTags.includes(b.tag) ? 1 : 0;
        return bPrior - aPrior;
      });
    }

    let itterate = true;
    let currentTagIndex = 0;
    let currentDurationInMinutes = 0;

    const usedPlacesIDs: string[] = [];

    while (itterate) {
      const selectedPlaces = response.map((item) => item.selected).flat(1);
      if (selectedPlaces.length === maxPlacesCount) {
        itterate = false;
        break;
      }

      const usedIds: string[] = uniq([...usedPlacesIDs]);

      /* Check if we have not checked places for all tags */
      if (usedIds.length === placesIds.length) {
        itterate = false;
        break;
      }

      const data = response[currentTagIndex];
      const nextIndex = Boolean(response.at(currentTagIndex + 1))
        ? currentTagIndex + 1
        : 0;

      /* Check if we have not checked places for current tag */
      const notUsedPlaces = data.available.filter(
        (item) => !usedIds.includes(item.place_id)
      );

      if (notUsedPlaces.length === 0) {
        currentTagIndex = nextIndex;
        continue;
      }

      for (const place of notUsedPlaces) {
        if (usedIds.includes(place.place_id)) {
          continue;
        }

        const breaktime = place.breaktime ?? 0;
        const currentTime = currentDurationInMinutes + breaktime;

        usedIds.push(place.place_id);

        if (currentTime <= maxTime) {
          // eslint-disable-next-line no-loop-func
          response = response.map((item, itemIndex) => {
            if (itemIndex === currentTagIndex) {
              return { ...item, selected: [...item.selected, place] };
            }

            return item;
          });

          // selectedPlaces.push(place);

          currentDurationInMinutes = currentTime;
          break;
        }
      }

      currentTagIndex = nextIndex;
      usedIds.forEach((item) => usedPlacesIDs.push(item));
    }

    // const response: Response = { usedTags: uniq(usedTags), selectedPlaces };
    this.lastRequest = props;
    this.lastResponse = response;

    return response;
  }

  public async reduceOneDay() {
    if (!this.lastRequest || !this.lastResponse) {
      return Promise.reject(
        new Error("reduceOneDay(): Last request/response is empty.")
      );
    }

    const { daysCount, options } = this.lastRequest;
    const { roadDayTime = DEFAULT_ROUTE_DAY_HOURS } = options ?? {};

    const reducedDaysCount = daysCount - 1 === 0 ? 1 : daysCount - 1;
    const oneDayInMinutes = roadDayTime * 60;

    let sortedPlaces = Utils.Places.sortByCustomRating(
      this.lastResponse
        .filter((item) => !this.prioritizedTags.includes(item.tag))
        .map((item) => item.selected)
        .flat(1)
    );

    const placesToRemove: Iterable<IPlace>[] = [];

    if (sortedPlaces.length > 0) {
      sortedPlaces = [...sortedPlaces].reverse();
    } else {
      const sorted = Utils.Places.sortByCustomRating(
        this.lastResponse.map((item) => item.selected).flat(1)
      );
      sortedPlaces = [...sorted].reverse();
    }

    for (const [index, place] of sortedPlaces.entries()) {
      const breaktimes = sortedPlaces
        .slice(0, index + 1)
        .reduce((acc, item) => acc + item.breaktime!, 0);

      if (breaktimes > oneDayInMinutes) break;
      placesToRemove.push(place);
    }

    const placesToRemoveIds = placesToRemove.map((item) => item.id);
    const response: Response = this.lastResponse.map((item) => ({
      ...item,
      selected: item.selected.filter(
        (item) => !placesToRemoveIds.includes(item.id)
      ),
    }));

    this.lastRequest = { ...this.lastRequest, daysCount: reducedDaysCount };
    this.lastResponse = response;

    return Promise.resolve(placesToRemove);
  }

  /* Private Methods */

  private async fetchCategories() {
    const categories = await api.firestore.categoryTags.list({
      onlyPublicCategories: false,
    });
    console.log({ categories });

    const values = this.selectedTags;
    const tags = categories.map((item) => item.tags).flat(1);
    const mapped = values.map((item) => tags.find((tag) => tag.name === item)!);
    this.categoryTags = mapped;

    return Promise.resolve();
  }
}
